/**
 * @file src/hooks/useScreenAnalytics.ts
 * @exports useResolvedScreenChange, useScreenAnalytics
 * @summary Tracks a `Screen Viewed` event each time the router resolves a new pathname.
 * @remarks Replaces Mixpanel's automatic `$mp_web_page_view`. `screen` is the matched route
 *          template (e.g. `/vid/additional-information/$beamId`) so dynamic segments don't
 *          fragment the report; unmatched URLs fall back to the raw pathname. Search-only
 *          navigations don't fire, and a lazy route fires once, after it resolves.
 *          `useResolvedScreenChange` exposes the same screen resolution to other analytics.
 */

import { rootRouteId, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { ANALYTICS, analytics } from "src/helpers";

interface UseResolvedScreenChangeArgs {
	onScreenChange: (screen: string) => void;
}

/** Calls `onScreenChange` with the route template (or raw pathname) each time a new pathname resolves. */
export const useResolvedScreenChange = ({ onScreenChange }: UseResolvedScreenChangeArgs): void => {
	const pathname = useRouterState({ select: (state) => state.resolvedLocation?.pathname });
	const screen = useRouterState({
		select: (state) => {
			const leaf = state.matches.at(-1);
			// Only the root route matched (an unmatched URL), so there is no route template to report.
			if (!leaf || leaf.routeId === rootRouteId) {
				return undefined;
			}
			return leaf.fullPath;
		},
	});

	// Matches settle before resolvedLocation does, so keying on screen as well would fire twice per lazy route.
	const screenRef = useRef(screen);
	screenRef.current = screen;
	const onScreenChangeRef = useRef(onScreenChange);
	onScreenChangeRef.current = onScreenChange;

	useEffect(() => {
		if (!pathname) {
			return;
		}
		onScreenChangeRef.current(screenRef.current ?? pathname);
	}, [pathname]);
};

/** Fires `Screen Viewed` for each resolved pathname. Mount once, in the root route component. */
export const useScreenAnalytics = (): void => {
	useResolvedScreenChange({ onScreenChange: (screen) => analytics.track(ANALYTICS.screenViewed, { screen }) });
};
